/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs, { Dayjs } from 'dayjs'
import _ from 'lodash'
import { useEffect, useState } from 'react'

import { attributesSelectors } from '@/entities/attributes'
import { AttributeTypeEnum } from '@/entities/attributes/models/Attribute'
import { depotsSelectors } from '@/entities/depots'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { ViewOnlyOrderStatuses } from '@/entities/gridTables/model/OrderStatusType'
import { ordersSelectors } from '@/entities/orders'
import { saveOrder } from '@/entities/orders/thunks/saveOrder'
import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { IconButton, ToggleButton } from '@/shared/components'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { SelectOption } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { CustomIcon } from '@/shared/components/Icon/CustomIcon'
import { OrderItemUpdate } from '@/shared/components/OrderItemsField/models/OrderItemUpdate'
import { OrderItemChangeStateEnum } from '@/shared/components/OrderItemsField/models/OrderItemUpdateState'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { timeDayjsToHhMm, timeDayjsToHhMmSs, timeHhMmSsToDayjs } from '@/shared/lib/timeHelper'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { CircularProgress, Divider, Grid, Typography } from '@mui/material'

import { getFormDefinitionForCourierModel } from '../OrderFormDefinitions/getFormDefinitionForCourierModel'
import { getFormDefinitionForDepotModel } from '../OrderFormDefinitions/getFormDefinitionForDepotModel'
import { OrderDetailsPlaceholder } from './OrderDetailsPlaceholder'
import { OrderHistoryTabPanel } from './OrderHistoryTabPanel'
import { StyledContainer, StyledContent, StyledHeader } from './StyledOrderDetailsSidebar'

export type OrderDetailsSidebarProps = {
  onCollapse: () => void
}

export const OrderDetailsSidebar: React.FC<OrderDetailsSidebarProps> = ({ onCollapse }) => {
  const dispatch = useAppDispatch()
  const selectedOrderId = useAppSelector((state) => state.userInterface.detailViewedOrderId)
  const activeGroupId = useAppSelector((state) => state.userInterface.activeGroupId)
  const selectedOrder = useAppSelector((state) => ordersSelectors.selectById(state, selectedOrderId || ''))
  const businessModel = useAppSelector((state) => state.user.businessModel)
  const isCourierModel = businessModel === BusinessModelEnum.Courier
  const isDepotModel = businessModel === BusinessModelEnum.Depot
  const [isEditMode, setEditMode] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [orderFormValues, setOrderFormValues] = useState<Record<string, unknown>>({})
  const { setToastContent } = useToastContext()

  const depots = useAppSelector((state) => depotsSelectors.selectAll(state))
    .filter((depot) => depot.groupId === activeGroupId)
  const depotOptions: SelectOption[] = depots.map((depot) => ({ label: depot.name, value: depot.id }))
  const ordersAttributes = useAppSelector((state) => attributesSelectors.selectAll(state))
    .filter((attribute) => attribute.attributeType === AttributeTypeEnum.Order)
  const orderAttributeOptions: SelectOption[] = ordersAttributes.map((attribute) => ({ label: attribute.name, value: attribute.id }))

  // Retreive the form definition based on the business model.
  //   The form definition contains the fields of the form used for DynamicFormRenderer.
  const depotModelFormDefinition = getFormDefinitionForDepotModel({
    formType: 'tabs',
    defaultValues: {},
    selectOptions: {
      'depot': depotOptions,
      'attributes': orderAttributeOptions,
    },
    additionalTabs: [
      {
        label: 'History',
        tabPanelContent: <OrderHistoryTabPanel orderId={selectedOrderId} />,
      },
    ],
  })

  const courierModelFormDefinition = getFormDefinitionForCourierModel({
    formType: 'tabs',
    defaultValues: {},
    selectOptions: {
      'attributes': orderAttributeOptions,
    },
    additionalTabs: [
      {
        label: 'History',
        tabPanelContent: <OrderHistoryTabPanel orderId={selectedOrderId} />,
      },
    ],
  })

  const formDefinition = isCourierModel ? courierModelFormDefinition : depotModelFormDefinition

  // Check if the selected order is part of the view only statuses
  const isOrderViewOnly = ViewOnlyOrderStatuses.includes(selectedOrder?.statusType)

  useEffect(() => {
    if (isOrderViewOnly) {
      setEditMode(false)
    }
  }, [isOrderViewOnly])

  // Construct the initial form values based on the selected order
  useEffect(() => {
    if (!selectedOrder) return

    if (isCourierModel) {
      // TODO: populate data for the courier model as well
      const orderValues = {
        ...selectedOrder,
        twStart: selectedOrder?.timeWindows[0]?.twStart,
        twStartNextDay: selectedOrder?.timeWindows[0]?.twStartNextDay,
        twFinish: selectedOrder?.timeWindows[0]?.twFinish,
        twFinishNextDay: selectedOrder?.timeWindows[0]?.twFinishNextDay,
        twStartPickup: selectedOrder?.timeWindows[0]?.twStart,
        twStartNextDayPickup: selectedOrder?.timeWindows[0]?.twStartNextDay,
        twFinishPickup: selectedOrder?.timeWindows[0]?.twFinish,
        twFinishNextDayPickup: selectedOrder?.timeWindows[0]?.twFinishNextDay,
        twStartDelivery: selectedOrder?.timeWindows[1]?.twStart,
        twStartNextDayDelivery: selectedOrder?.timeWindows[1]?.twStartNextDay,
        twFinishDelivery: selectedOrder?.timeWindows[1]?.twFinish,
        twFinishNextDayDelivery: selectedOrder?.timeWindows[1]?.twFinishNextDay,
        priority: selectedOrder.priority,
        serviceTime: selectedOrder.isPickup
          ? selectedOrder.serviceTimePickup
          : selectedOrder.serviceTimeDelivery,
        isPickup: selectedOrder.isPickup ? '1' : '0',
        depot: depotOptions.find((depot) => depot.value === selectedOrder.toLocation?.id),
        address: selectedOrder.isPickup
          ? selectedOrder.fromLocation
          : selectedOrder.toLocation,
        customerExternalId: selectedOrder.isPickup
          ? (selectedOrder.fromLocation as any)?.externalId
          : (selectedOrder.toLocation as any)?.externalId,
        attributes: selectedOrder.attributeIds
          ? selectedOrder.attributeIds.map((attributeId) => {
            return orderAttributeOptions.find((attribute) => attribute.value === attributeId)
          })
          : [],
        orderItems: selectedOrder.orderItems.map((orderItem) => {
          return {
            ...orderItem,
            isDataValid: true,
            changeState: OrderItemChangeStateEnum.Unchanged,
          }
        }),
      }

      setOrderFormValues(orderValues)
    } else {
      const customerLocation = selectedOrder.isPickup ? selectedOrder.fromLocation : selectedOrder.toLocation
      const depotLocation = selectedOrder.isPickup ? selectedOrder.toLocation : selectedOrder.fromLocation
      const timeWindow = selectedOrder.timeWindows.length > 0 ? selectedOrder.timeWindows[0] : null

      const orderValues = {
        // Order Detail Fields
        orderNumber: selectedOrder.orderNumber,
        depot: depotOptions.find((depot) => depot.value === depotLocation?.id),
        consignmentNumber: selectedOrder.consignmentNumber,
        isPickup: selectedOrder.isPickup ? '1' : '0',
        preferredDateOfOperation: dayjs(selectedOrder.preferredDateOfOperation, 'DD/MM/YYYY'),
        attributes: selectedOrder.attributeIds
          ? selectedOrder.attributeIds.map((attributeId) => {
            return orderAttributeOptions.find((attribute) => attribute.value === attributeId)
          }) : [],
        tripNumber: selectedOrder.tripNumber,
        description: selectedOrder.description,
        deliveryInstruction: selectedOrder.deliveryInstruction,
        comment: selectedOrder.comment,
        weight: selectedOrder.weight,
        volume: selectedOrder.volume,
        quantity: selectedOrder.quantity,

        // Customer Detail Fields
        locationName: customerLocation?.name,
        customerExternalId: customerLocation?.externalId,
        locationContact: customerLocation?.contact,
        emailPickup: selectedOrder.emailPickup,
        emailDelivery: selectedOrder.emailDelivery,
        phonePickup: selectedOrder.phonePickup,
        phoneDelivery: selectedOrder.phoneDelivery,
        smsPickup: selectedOrder.smsPickup,
        smsDelivery: selectedOrder.smsDelivery,

        // Scheduling Fields
        maxOnBoardTime: timeHhMmSsToDayjs(selectedOrder?.maxOnBoardTime),
        priority: selectedOrder.priority,
        twStart: timeHhMmSsToDayjs(timeWindow?.twStart),
        twStartNextDay: timeWindow?.twStartNextDay,
        twFinish: timeHhMmSsToDayjs(timeWindow?.twFinish),
        twFinishNextDay: timeWindow?.twFinishNextDay,
        serviceTime: selectedOrder.isPickup
          ? timeHhMmSsToDayjs(selectedOrder.serviceTimePickup)
          : timeHhMmSsToDayjs(selectedOrder.serviceTimeDelivery),
        address: customerLocation,
        orderItems: selectedOrder.orderItems.map((orderItem) => {
          return {
            ...orderItem,
            hasActions: true,
            isDataValid: true,
            changeState: OrderItemChangeStateEnum.Unchanged,
          }
        }),
      }

      setOrderFormValues(orderValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder])

  const onSubmit = (data: Record<string, unknown>) => {
    if (!selectedOrderId) return

    setSubmitting(true)

    const postVal = _.cloneDeep(data)

    // Make API call to create order after making the relevant data transformations
    postVal.id = selectedOrderId
    postVal.isPickup = data.isPickup === '1'
    postVal.orderItems = []
    postVal.attributeIds = (data.attributes as DropdownOption[])?.map((attribute) => attribute.value)

    const depotId = (data.depot as DropdownOption)?.value

    // For Depot Model, there is only one customer address
    if (isDepotModel) {
      // Time window
      const timeWindows = []

      timeWindows.push({
        twStart: timeDayjsToHhMm(data.twStart as Dayjs),
        twStartNextDay: data.twStartNextDay,
        twFinish: timeDayjsToHhMm(data.twFinish as Dayjs),
        twFinishNextDay: data.twFinishNextDay,
        isPickup: data.isPickup,
      })
      postVal.timeWindows = timeWindows

      // Order items
      postVal.orderItems = (data.orderItems as OrderItemUpdate[]).map((orderItem) => {
        if (orderItem.state === OrderItemChangeStateEnum.Added) {
          const { id, ...newOrderItem } = orderItem

          return newOrderItem
        }

        return orderItem
      })

      // From and to Addresses
      if (postVal.isPickup) {
        postVal.serviceTimePickup = timeDayjsToHhMmSs(data.serviceTime as Dayjs)
        postVal.fromLocation = postVal.address
        if (postVal.fromLocation)
          (postVal.fromLocation as any).externalId = postVal.customerExternalId
        postVal.toLocation = depots.find((depot) => depot.id === depotId)
      } else {
        postVal.serviceTimeDelivery = timeDayjsToHhMmSs(data.serviceTime as Dayjs)
        postVal.fromLocation = depots.find((depot) => depot.id === depotId)
        postVal.toLocation = postVal.address
        if (postVal.toLocation)
          (postVal.toLocation as any).externalId = postVal.customerExternalId
      }

      delete postVal.attributes
      delete postVal.depot
      delete postVal.address
      delete postVal.customerExternalId
      delete postVal.twStart
      delete postVal.twStartNextDay
      delete postVal.twFinish
      delete postVal.twFinishNextDay
    }

    // For Courier Model, there are two addresses. One for pickup and one for delivery.
    if (isCourierModel) {
      const timeWindows = []

      timeWindows.push({
        twStart: timeDayjsToHhMm(data.twStartPickup as Dayjs),
        twStartNextDay: data.twStartNextDayPickup,
        twFinish: timeDayjsToHhMm(data.twFinishPickup as Dayjs),
        twFinishNextDay: data.twFinishNextDayPickup,
        isPickup: true,
      })
      timeWindows.push({
        twStart: timeDayjsToHhMm(data.twStartDelivery as Dayjs),
        twStartNextDay: data.twStartNextDayDelivery,
        twFinish: timeDayjsToHhMm(data.twFinishDelivery as Dayjs),
        twFinishNextDay: data.twFinishNextDayDelivery,
        isPickup: false,
      })
    }

    // Make API call to create order and handle the response
    dispatch(saveOrder({ order: postVal }))
      .unwrap()
      .then((newOrderResult) => {
        if (newOrderResult) {
          setToastContent({
            open: true,
            severity: 'success',
            title: 'Order Updated',
            content: `Order ${newOrderResult.orderNumber} has been updated successfully.`,
          })
          setSubmitting(false)
        }
      }).finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <Grid container justifyContent="space-between">
          <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography sx={{ marginLeft: pxToRem(8) }} variant="h2">
              {isEditMode ? 'Edit' : 'View'} Order{selectedOrder?.orderNumber && `: ${selectedOrder.orderNumber}`}
            </Typography>
          </Grid>
          <Grid item sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
            <ToggleButton
              value="checked"
              primary
              text
              disabled={isOrderViewOnly}
              selected={isEditMode}
              onChange={() => {
                setEditMode((prev) => !prev)
              }}
            >
              {isEditMode ? 'Cancel Edit' : 'Edit Order'}
            </ToggleButton>
            <IconButton
              onClick={onCollapse}
              round={false}
              sx={{
                margin: 0,
              }}
            >
              <CustomIcon svg iconCode={'rightSidebarCollapse'} width={pxToRem(16)} />
            </IconButton>
          </Grid>
        </Grid>
      </StyledHeader>
      <Divider />
      <StyledContent>
        {selectedOrder ? (
          <DynamicForm
            {...formDefinition}
            initialValues={orderFormValues}
            onSubmit={onSubmit}
            isReadOnly={!isEditMode}
            sx={{ height: '100%' }}
            slotProps={{
              submitButton: {
                disabled: isSubmitting,
              },
            }}
            submitContent={isSubmitting ? <CircularProgress size="1rem" /> : 'Save'}
          >
          </DynamicForm>
        ) : (
          <OrderDetailsPlaceholder />
        )}
      </StyledContent>
    </StyledContainer>
  )
}
